import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { trackEvent, setUserProperties } from "../utils/analytics";
import en from "../locales/en.json";
import zhCN from "../locales/zh-CN.json";
import ko from "../locales/ko.json";
import de from "../locales/de.json";
import fr from "../locales/fr.json";
import ja from "../locales/ja.json";
import it from "../locales/it.json";

export type Language = "en-GB" | "en" | "zh-CN" | "ko" | "de" | "fr" | "ja" | "it";

export const LANGUAGES: { code: Language; flag: string; name: string }[] = [
  { code: "en-GB", flag: "🇬🇧", name: "English (UK)" },
  { code: "en", flag: "🇺🇸", name: "English (Simplified)" },
  { code: "zh-CN", flag: "🇨🇳", name: "中文" },
  { code: "ko", flag: "🇰🇷", name: "한국어" },
  { code: "de", flag: "🇩🇪", name: "Deutsch" },
  { code: "fr", flag: "🇫🇷", name: "Français" },
  { code: "ja", flag: "🇯🇵", name: "日本語" },
  { code: "it", flag: "🇮🇹", name: "Italiano" },
];

const translations: Record<Language, Record<string, any>> = {
  "en-GB": en,
  en,
  "zh-CN": zhCN,
  ko,
  de,
  fr,
  ja,
  it,
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function getNestedValue(obj: any, path: string): string | undefined {
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem("language");
    if (saved === "en-GB" || saved === "en" || saved === "zh-CN" || saved === "ko" || saved === "de" || saved === "fr" || saved === "ja" || saved === "it") return saved;
    if (navigator.language.startsWith("zh")) return "zh-CN";
    if (navigator.language.startsWith("ko")) return "ko";
    if (navigator.language.startsWith("de")) return "de";
    if (navigator.language.startsWith("fr")) return "fr";
    if (navigator.language.startsWith("ja")) return "ja";
    if (navigator.language.startsWith("it")) return "it";
    return "en-GB";
  });

  const handleSetLanguage = (newLang: Language) => {
    if (newLang !== language) {
      trackEvent('language_change', {
        language_from: language,
        language_to: newLang,
      });
    }
    setLanguage(newLang);
  };

  useEffect(() => {
    localStorage.setItem("language", language);
    document.documentElement.lang = language;
    setUserProperties({ app_language: language });
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within a LanguageProvider");
  return context;
};

export function useTranslation() {
  const { language } = useLanguage();

  const t = useCallback((key: string, vars?: Record<string, string | number>): string => {
    const value = getNestedValue(translations[language], key) ?? getNestedValue(translations.en, key) ?? key;
    if (!vars) return value;
    return Object.entries(vars).reduce<string>(
      (str, [k, v]) => str.replace(new RegExp(`\\{${k}\\}`, "g"), String(v)),
      value
    );
  }, [language]);

  return { t };
}
